import { CommonModule } from '@angular/common';
import {
    Component,
    EventEmitter,
    inject,
    Input,
    OnDestroy,
    OnInit,
    Output,
    TemplateRef,
    ViewChild,
    ViewEncapsulation,
} from '@angular/core';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { NgSelectComponent, NgSelectModule } from '@ng-select/ng-select';
import { of, Subject } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { CodaltComponent } from 'src/app/codalt.component';
import { FormattedAddress, LocationService } from 'src/app/services/location.service';

export interface LocationSelectResult {
    type: string;
    label: string;
    sublabel?: string;
    lat: number | null;
    lng: number | null;
    address?: FormattedAddress;
    data?: unknown;
}

@Component({
    selector: 'app-location-select',
    standalone: true,
    imports: [CommonModule, ReactiveFormsModule, NgSelectModule],
    templateUrl: './location-select.component.html',
    styleUrl: './location-select.component.scss',
    encapsulation: ViewEncapsulation.None,
})
export class LocationSelectComponent extends CodaltComponent implements OnInit, OnDestroy {
    @Input({ required: true }) control!: FormControl;
    @Input() appearance: 'fill' | 'inline' = 'fill';
    @Input() placeholder = 'Locatie';
    @Input() selectClass = '';
    @Input() minTermLength = 3;
    @Input() typeToSearchText = `Typ minimaal ${this.minTermLength} karakters om te zoeken`;
    @Input() notFoundText = 'Er zijn geen resultaten gevonden';
    @Input() source = 'LocationSelectComponent';
    @Input() resultFilter: 'none' | 'localityOrTown' | 'streetAndLocality' = 'none';
    @Input() labelTemplate?: TemplateRef<any>;
    @Input() optionTemplate?: TemplateRef<any>;

    @Output() addressSelected = new EventEmitter<FormattedAddress | null>();

    @ViewChild('locationSelect') locationSelect?: NgSelectComponent;

    private locationService = inject(LocationService);

    locationInput$ = new Subject<string>();
    locations: LocationSelectResult[] = [];
    loading = false;
    currentSearchTerm = '';

    ngOnInit(): void {
        this.showDefaultLocations();

        this.subscriptions.add(
            this.locationService
                .searchLocations(this.locationInput$, this.source, this.minTermLength)
                .pipe(
                    catchError(() => of([])),
                    map(addresses => {
                        if (this.resultFilter === 'localityOrTown') {
                            return addresses.filter(address => address.locality || address.town);
                        }

                        if (this.resultFilter === 'streetAndLocality') {
                            return addresses.filter(address => address.street && address.locality);
                        }

                        return addresses;
                    }),
                    map(addresses => this.mapAddressesToResults(addresses))
                )
                .subscribe(results => {
                    this.loading = false;
                    this.locations = this.includeSelectedLocationIfNeeded(results);
                })
        );
    }

    showDefaultLocations(openDropdown = false): void {
        if (this.currentSearchTerm) {
            return;
        }

        this.loading = false;
        this.locations = this.control?.value
            ? this.includeSelectedLocationIfNeeded([])
            : this.mapAddressesToResults(this.locationService.getDefaultLocations());

        if (openDropdown) {
            setTimeout(() => this.locationSelect?.open());
        }
    }

    onSearch(term: string): void {
        const search = (term ?? '').trim();

        this.currentSearchTerm = search;
        this.loading = search.length >= this.minTermLength;
        this.locationInput$.next(search);
    }

    onSelection(location?: LocationSelectResult): void {
        this.addressSelected.emit(location?.address ?? null);
    }

    trackByFn(item: LocationSelectResult): string {
        return `${item.type}-${item.label}`;
    }

    private includeSelectedLocationIfNeeded(results: LocationSelectResult[]): LocationSelectResult[] {
        const selectedLocation = this.control?.value;

        if (!selectedLocation || results.some(location => location.label === selectedLocation)) {
            return results;
        }

        return [
            {
                type: 'address',
                label: selectedLocation,
                lat: null,
                lng: null,
                address: { formattedAddress: selectedLocation } as FormattedAddress,
            },
            ...results,
        ];
    }

    private mapAddressesToResults(addresses: FormattedAddress[]): LocationSelectResult[] {
        return addresses.map(address => ({
            type: 'address',
            label: address.formattedAddress,
            lat: address.lat ?? null,
            lng: address.lng ?? null,
            address,
        }));
    }
}
